import {
  ClassificationFacts,
  classifyComponentType,
  ComponentType,
} from './ffsComponentClassification'
import {
  CorrosionType,
  EntryRoute,
  FfsPartLevelEligibility,
  FfsRegionClassification,
  FfsRegionClassificationSchema,
  ProfileRequirement,
} from './ffsRegionClassificationSchema'
import { FfsPart } from './ffsTypes'

/**
 * Domain helpers for the per-LTA FFS pre-planning classification: corrosion
 * type (Rule 1), the type→eligibility matrix (Rule 3), route/profile selection
 * (Rules 4–9), record construction, and the cross-field domain validator.
 *
 * The classification RULES themselves live in `ffsComponentClassification.ts`;
 * this module composes them onto a persisted decision and validates it.
 */

/**
 * API 579-1/ASME FFS-1 (2021) §4.3.3.2 Eq. 4.1: point thickness readings are
 * valid only when the minimum measured thickness is within 10% of the average
 * — i.e. `t_mm >= 0.9 * t_am`.
 */
export const POINT_READING_UNIFORMITY_RATIO = 0.9

/**
 * The planform shape of an assessment region, per API 579-1 Part 5 §5.2.1. NOT
 * persisted and NOT a direct routing input — it is the second gate of the
 * corrosion-type decision, consulted only on the NON-uniform side: a groove-like
 * footprint on an otherwise-local region is a separate, out-of-scope procedure
 * (`'groove-like'` corrosion type), while a compact footprint stays ordinary
 * local metal loss.
 */
export type FlawShape = 'lta' | 'groove-like'

/**
 * The length-to-width ratio at which a region stops being a local thin area and
 * becomes groove-like.
 *
 * API 579-1 Part 5 §5.2.1 does NOT state a number: §5.2.1(a) defines an LTA as
 * one whose "length ... is the same order of magnitude as the width", and
 * §5.2.1(b)(1) defines a Groove as one whose "length ... is much greater than
 * the width". 10 is OUR operationalization of "same order of magnitude" — an
 * order of magnitude being 10x. Do not cite it to the standard as a stated limit.
 */
export const FLAW_ASPECT_RATIO_LIMIT = 10

/**
 * Rule 2 — flaw shape from the planform aspect ratio (§5.2.1). `length/width <=
 * 10` → `'lta'`; `>` → `'groove-like'`. Returns `null` when the geometry is
 * missing/invalid — the engineer then classifies.
 *
 * `lengthMm` is the LONGER extent and `widthMm` the shorter; callers establish
 * that with max/min. The ratio is deliberately axis-FREE: §5.2.1 speaks only of
 * the flaw's own length and width, and §5.2.1(b)(1) attributes grooves to
 * *directional* erosion or corrosion without fixing the direction — so a
 * circumferential groove counts exactly as much as a longitudinal one.
 */
export function classifyFlawShape({
  lengthMm,
  widthMm,
}: {
  lengthMm: number | null | undefined
  widthMm: number | null | undefined
}): FlawShape | null {
  if (
    lengthMm === null ||
    lengthMm === undefined ||
    widthMm === null ||
    widthMm === undefined ||
    !Number.isFinite(lengthMm) ||
    !Number.isFinite(widthMm) ||
    widthMm <= 0 ||
    lengthMm < widthMm
  ) {
    return null
  }
  return lengthMm / widthMm <= FLAW_ASPECT_RATIO_LIMIT ? 'lta' : 'groove-like'
}

/**
 * WHICH gate decided the corrosion type. Returned rather than left implicit so
 * that "why is this local?" has exactly ONE owner: the readout renders this
 * instead of re-running the uniformity test to work the answer out backwards,
 * which would put the rule in two places and let them drift.
 */
export type CorrosionBasis =
  /** No usable thickness statistics — nothing to test. */
  | 'no-thickness'
  /** Gate 1 passed: `t_mm >= 0.9*t_am` (§4.3.3.2 Eq. 4.1) — shape is irrelevant. */
  | 'uniform'
  /** Gate 1 failed, and either compact or geometry-less (defaults to local). */
  | 'non-uniform'
  /** Gate 1 failed AND gate 2 failed: non-uniform AND elongated (§5.2.1(b)(1)). */
  | 'groove-like'

/**
 * The corrosion verdict paired with its reason. A discriminated union so an
 * inconsistent pairing (e.g. `general` because `non-uniform`) is unrepresentable.
 */
export type CorrosionVerdict =
  | { corrosionType: null; basis: 'no-thickness' }
  | { corrosionType: 'general'; basis: 'uniform' }
  | { corrosionType: 'local'; basis: 'non-uniform' }
  | { corrosionType: 'groove-like'; basis: 'groove-like' }

/**
 * Rule 1 — corrosion type, from TWO gates evaluated in a fixed order:
 *
 *  1. Thickness uniformity (§4.3.3.2, Eq. 4.1): `t_mm >= 0.9*t_am`. Passing this
 *     is `general` OUTRIGHT — the planform is irrelevant, so no geometry is ever
 *     needed to reach `general`.
 *  2. Planform (§5.2.1), consulted ONLY on the non-uniform side: a groove-like
 *     footprint (§5.2.1(b)(1)) is a separate, out-of-scope disposition
 *     (`'groove-like'`); a compact footprint, or one with no usable geometry, is
 *     ordinary `local` metal loss (§5.2.1(a)).
 *
 * ORDER MATTERS, but the other way round from before: uniformity is checked
 * FIRST, and only a uniformity FAILURE ever consults `flawShape`. Geometry is
 * therefore needed only to ESCAPE `local` into `groove-like` — never to reach
 * `general` — so missing/invalid geometry (`flawShape === null`) DEFAULTS to
 * `local` rather than falling through to undetermined.
 */
export function classifyCorrosionType({
  tMmMm,
  tAmMm,
  flawShape,
}: {
  tMmMm: number | null | undefined
  tAmMm: number | null | undefined
  flawShape: FlawShape | null
}): CorrosionVerdict {
  if (
    tMmMm === null ||
    tMmMm === undefined ||
    tAmMm === null ||
    tAmMm === undefined ||
    !Number.isFinite(tMmMm) ||
    !Number.isFinite(tAmMm) ||
    tAmMm <= 0
  ) {
    return { corrosionType: null, basis: 'no-thickness' }
  }
  // Gate 1 — uniformity. Passing it is general, whatever the planform.
  if (tMmMm >= POINT_READING_UNIFORMITY_RATIO * tAmMm) {
    return { corrosionType: 'general', basis: 'uniform' }
  }
  // Non-uniform: local metal loss, UNLESS the planform is explicitly groove-like
  // (§5.2.1(b)(1)). Missing/invalid geometry (`flawShape === null`) defaults to
  // local rather than undetermined — only an explicit >10:1 ratio escapes it.
  return flawShape === 'groove-like'
    ? { corrosionType: 'groove-like', basis: 'groove-like' }
    : { corrosionType: 'local', basis: 'non-uniform' }
}

/**
 * Rule 3 — the exact per-type eligibility matrix (§4.2.5, §4.4.3.3, Part 5
 * §5.2.5(d)/§5.2.6, Restriction 9). Derived from the component type and NOT
 * engineer-editable. `recommended` is left `false` here (set to the chosen
 * `entryRoute` level by `buildRegionClassification`).
 */
const MATRIX: Record<
  ComponentType,
  { p4l1: boolean; p4l2: boolean; p5l1: boolean; p5l2: boolean }
> = {
  A: { p4l1: true, p4l2: true, p5l1: true, p5l2: true },
  B1: { p4l1: false, p4l2: true, p5l1: false, p5l2: true },
  B2: { p4l1: false, p4l2: true, p5l1: false, p5l2: false },
  C: { p4l1: false, p4l2: false, p5l1: false, p5l2: false },
}

export function componentTypeEligibility(
  type: ComponentType
): FfsPartLevelEligibility {
  const m = MATRIX[type]
  const lvl = (eligible: boolean) => ({ eligible, recommended: false })
  return {
    [FfsPart.PART_4]: { level1: lvl(m.p4l1), level2: lvl(m.p4l2) },
    [FfsPart.PART_5]: { level1: lvl(m.p5l1), level2: lvl(m.p5l2) },
  }
}

/**
 * The type's entry level in a given part: the LOWEST eligible level (A→L1,
 * B1/B2→L2 for Part 4; A→L1, B1→L2 for Part 5), or `null` when the type cannot be
 * assessed in that part at all (Type C anywhere; B2 under Part 5). Derived from
 * `MATRIX` via `componentTypeEligibility` so it can never disagree with it.
 *
 * The single owner of "which level do I start at in THIS part?", shared by the
 * per-LTA execution gate (`computeFfsEligibility`) and the calc card's Part-4
 * completion check — so the two derive the entry level identically.
 */
export function partEntryLevel(
  type: ComponentType,
  part: FfsPart
): 'level1' | 'level2' | null {
  const e = componentTypeEligibility(type)
  if (e[part].level1.eligible) return 'level1'
  if (e[part].level2.eligible) return 'level2'
  return null
}

/**
 * The permitted Part-4 entry level for a type: the LOWEST Part-4 level the type
 * is eligible for, else `NEITHER_APPLICABLE`. Derived from `MATRIX` rather than
 * hand-maintained so the two can never disagree (A→L1, B1/B2→L2, C→NEITHER
 * falls out of the matrix rows). `ffsRegionRouting.test.ts` pins all four
 * mappings literally, so a MATRIX edit that changes a route fails loudly.
 */
export function selectPart4EntryRoute(type: ComponentType): EntryRoute {
  const m = MATRIX[type]
  if (m.p4l1) return 'PART_4_LEVEL_1'
  if (m.p4l2) return 'PART_4_LEVEL_2'
  return 'NEITHER_APPLICABLE'
}

/**
 * Rules 4–9 — the corrosion-driven entry route for an LTA. Eligibility is
 * type-only (the matrix); this picks which *permitted* route the engineer
 * executes, per API 579's method split:
 *  - A groove-like flaw → `GROOVE_NOT_COVERED`, regardless of type — this
 *    dominates the Type C check below, so the validator and the router can
 *    never disagree about which terminal a groove-like region gets.
 *  - Type C → `NEITHER_APPLICABLE`.
 *  - general metal loss → the Part-4 entry (point readings valid).
 *  - local metal loss → Part 5 for a Part-5-eligible type (A→L1, B1→L2);
 *    B2 has no Part 5 → Part 4 L2.
 *
 * TOTAL: every (type, corrosion) pair maps to a route. The separate question of
 * whether a local-metal-loss region *has* the critical-thickness profile its
 * Part-5 route requires is NOT decided here — `computeLtaDecision`
 * (`ffsLtaDecision.ts`) is the single owner of that gate, and it resolves it
 * before ever asking for a route. Keeping the gate in one layer is why this
 * function needs no `profileStatus` and never returns `null`.
 */
export function selectEntryRoute({
  componentType,
  corrosionType,
}: {
  componentType: ComponentType
  corrosionType: CorrosionType
}): EntryRoute {
  if (corrosionType === 'groove-like') return 'GROOVE_NOT_COVERED'
  if (componentType === 'C') return 'NEITHER_APPLICABLE'
  if (corrosionType === 'general') return selectPart4EntryRoute(componentType)
  // local metal loss:
  if (componentType === 'B2') return 'PART_4_LEVEL_2' // B2 has no Part 5
  return componentType === 'A' ? 'PART_5_LEVEL_1' : 'PART_5_LEVEL_2'
}

/**
 * Local metal loss mandates thickness profiles before ANY assessment (not just
 * Part 5). This is a requirement flag, never a Part-5 selector. A groove-like
 * flaw is never assessed by this tool at all, so it needs no profile either.
 */
export function profileRequirement(
  corrosionType: CorrosionType
): ProfileRequirement {
  return corrosionType === 'local' ? 'REQUIRED' : 'NOT_REQUIRED'
}

/**
 * Map an `entryRoute` to its (part, level), or null for a route with no part —
 * `NEITHER_APPLICABLE` (Type C, Level 3 required) and `GROOVE_NOT_COVERED` (a
 * separate out-of-scope procedure) both resolve to `null`, and downstream
 * (`computeFfsEligibility`) locks both levels identically for either.
 */
export function entryRouteToPartLevel(
  route: EntryRoute
): { part: FfsPart; level: 'level1' | 'level2' } | null {
  switch (route) {
    case 'PART_4_LEVEL_1':
      return { part: FfsPart.PART_4, level: 'level1' }
    case 'PART_4_LEVEL_2':
      return { part: FfsPart.PART_4, level: 'level2' }
    case 'PART_5_LEVEL_1':
      return { part: FfsPart.PART_5, level: 'level1' }
    case 'PART_5_LEVEL_2':
      return { part: FfsPart.PART_5, level: 'level2' }
    case 'NEITHER_APPLICABLE':
      return null
    case 'GROOVE_NOT_COVERED':
      return null
  }
}

/**
 * Do two classifications agree on everything a saved FFS report is COMPUTED
 * against — the component type, corrosion type, and entry route? A change to any
 * of these invalidates prior reports (they must be re-run, and close-out's
 * staleness gate flags them). A change to anything else — a corrected fact label
 * that doesn't move the derived type/route, `decidedBy`, `decidedAt` — leaves
 * prior reports valid.
 *
 * Used to decide whether re-saving a classification should bump `decidedAt` (the
 * staleness anchor): only when a report-affecting field actually changed, so an
 * idempotent or cosmetic re-save doesn't needlessly strand every report.
 */
export function reportAffectingClassificationEquals(
  a: Pick<
    FfsRegionClassification,
    'componentType' | 'corrosionType' | 'entryRoute'
  >,
  b: Pick<
    FfsRegionClassification,
    'componentType' | 'corrosionType' | 'entryRoute'
  >
): boolean {
  return (
    a.componentType === b.componentType &&
    a.corrosionType === b.corrosionType &&
    a.entryRoute === b.entryRoute
  )
}

/**
 * Compose a complete, valid `FfsRegionClassification` from a resolved type +
 * corrosion type + the chosen route. Sets the matrix eligibility and marks the
 * `entryRoute` level as `recommended`.
 *
 * The SINGLE constructor for the persisted record: `eligibility` and
 * `profileRequirement` are stored (see the docblock on those fields in
 * `ffsRegionClassificationSchema.ts`) but must always equal their derivations, so
 * no caller should be assembling the record by hand and risking a stale copy.
 */
export function buildRegionClassification(args: {
  componentType: ComponentType
  corrosionType: CorrosionType
  entryRoute: EntryRoute
  classificationFacts: ClassificationFacts
  decidedBy: string
  decidedAt: string
}): FfsRegionClassification {
  const eligibility = componentTypeEligibility(args.componentType)
  const pl = entryRouteToPartLevel(args.entryRoute)
  if (pl) eligibility[pl.part][pl.level].recommended = true
  return {
    componentType: args.componentType,
    corrosionType: args.corrosionType,
    entryRoute: args.entryRoute,
    eligibility,
    profileRequirement: profileRequirement(args.corrosionType),
    classificationFacts: args.classificationFacts,
    decidedBy: args.decidedBy,
    decidedAt: args.decidedAt,
  }
}

/**
 * Parse + domain-validate a raw `ffsRegionClassification` blob read off an
 * indication (the generic transport types it as `unknown`). Returns the typed,
 * consistent classification, or `null` if it is absent/malformed/inconsistent —
 * so downstream treats a bad record as "unclassified" (fail closed) rather than
 * trusting an unchecked cast. The Part-5 profile gate is a WRITE-time concern,
 * so it is intentionally not enforced here (no `profileAvailable` passed).
 */
export function parseRegionClassification(
  raw: unknown
): FfsRegionClassification | null {
  const parsed = FfsRegionClassificationSchema.safeParse(raw)
  if (!parsed.success) return null
  return validateFfsRegionClassification(parsed.data) === null
    ? parsed.data
    : null
}

/**
 * Cross-field domain validation for a committed classification: `null` when
 * valid, else the violated invariant as a message. Kept here (not inline in the
 * Zod schema) so it can call the classifier + matrix without a Zod↔classifier
 * cycle.
 *
 * Returns rather than throws so the read path (`parseRegionClassification`)
 * needs no try/catch — the one caller that must abort (the save in
 * `useFfsLtaPrePlanning`) raises the returned message itself.
 */
export function validateFfsRegionClassification(
  c: FfsRegionClassification,
  opts: { profileAvailable?: boolean } = {}
): string | null {
  // 1. The facts must classify to the recorded type.
  const classified = classifyComponentType(c.classificationFacts)
  if (classified.status !== 'resolved' || classified.type !== c.componentType) {
    return `Invalid FFS classification: facts do not classify to ${c.componentType}.`
  }

  // Needed by the drift guards below as well as by the route checks.
  const pl = entryRouteToPartLevel(c.entryRoute)

  // 2-3. STORED-VS-DERIVED DRIFT GUARDS. `eligibility` is persisted for
  // out-of-repo consumers (see the field docblock in
  // `ffsRegionClassificationSchema.ts`), which is only safe while the stored copy
  // provably equals its derivation — that is what these checks buy.
  //
  // They deliberately run BEFORE the Type C branch below, which returns early: a
  // guard placed after it would never run for Type C, the one type whose matrix
  // is entirely `false` and so the one most worth pinning.
  const canonical = componentTypeEligibility(c.componentType)
  for (const part of [FfsPart.PART_4, FfsPart.PART_5]) {
    for (const level of ['level1', 'level2'] as const) {
      const stored = c.eligibility[part][level]
      // 2. `eligible` must exactly equal the canonical matrix.
      if (stored.eligible !== canonical[part][level].eligible) {
        return `Invalid FFS classification: eligibility[${part}].${level} does not match the ${c.componentType} matrix.`
      }
      // 3. `recommended` must mark exactly the `entryRoute`'s level and nothing
      // else (nothing at all for NEITHER_APPLICABLE). Combined with checks 2 and
      // 5 this also gives `recommended ⇒ eligible`.
      const isEntryLevel = pl?.part === part && pl.level === level
      if (stored.recommended !== isEntryLevel) {
        return `Invalid FFS classification: eligibility[${part}].${level}.recommended does not match entryRoute ${c.entryRoute}.`
      }
    }
  }

  // 4. profileRequirement must match the corrosion type.
  if (c.profileRequirement !== profileRequirement(c.corrosionType)) {
    return 'Invalid FFS classification: profileRequirement does not match corrosion type.'
  }

  // 5. The entryRoute must be consistent with the type + matrix. NOTE: this
  // checks only that the route is *type-eligible* (in the matrix), NOT that it
  // equals the corrosion-driven `selectEntryRoute` pick — the permitted
  // alternatives (the UI's "also permitted" list) are intentionally valid
  // routes an engineer may commit. The form only ever saves `selectEntryRoute`'s
  // result today, so this looseness is deliberate headroom, not a gap.
  //
  // A groove-like flaw is checked BEFORE the Type C branch: `selectEntryRoute`
  // makes groove-like dominate the Type C check, so the validator must agree
  // regardless of `componentType` — a groove-like Type C region is still
  // GROOVE_NOT_COVERED, not NEITHER_APPLICABLE.
  if (c.corrosionType === 'groove-like') {
    return c.entryRoute === 'GROOVE_NOT_COVERED'
      ? null
      : 'Invalid FFS classification: groove-like must be GROOVE_NOT_COVERED.'
  }
  if (c.componentType === 'C') {
    return c.entryRoute === 'NEITHER_APPLICABLE'
      ? null
      : 'Invalid FFS classification: Type C must be NEITHER_APPLICABLE.'
  }
  if (pl === null) {
    return 'Invalid FFS classification: NEITHER_APPLICABLE is only valid for Type C.'
  }
  if (!canonical[pl.part][pl.level].eligible) {
    return `Invalid FFS classification: entryRoute ${c.entryRoute} is not eligible for ${c.componentType}.`
  }

  // 6. A Part-5 entryRoute requires a verified profile for the LTA.
  if (
    pl.part === FfsPart.PART_5 &&
    opts.profileAvailable !== undefined &&
    !opts.profileAvailable
  ) {
    return 'Invalid FFS classification: a Part 5 route requires a usable critical-thickness profile.'
  }

  return null
}
